import {
  Button,
  FormLabel,
  Modal,
  Select,
  Text,
  RadioOption,
  FileUpload,
  Switch,
  useToast,
  Toast,
  TextArea,
  RadioList,
  Tooltip,
  Radio,
} from "@oysterhr/ui"
import { ReactNode, useEffect, useMemo, useState } from "react"
import { Controller, UseFormReturn, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  AgreementStateEnum,
  AgreementTypeEnum,
  Employment,
  EnvelopeEventTypeEnum,
  EnvelopeStateEnum,
} from "@oysterhr/schema"
import * as Yup from "yup"
import { WarningNotificationBanner } from "./WarningNotificationBanner"
import { useFileUploadComponent } from "~/shared/hooks/useFileUploadComponent"
import { FilePayload } from "~/shared/hooks/useFileUploadComponent/types"
import { FileStatus } from "~/shared/hooks/useFileUploadComponent/utils"
import classNames from "classnames"
import { yupResolver } from "@hookform/resolvers/yup"
import { useUploadAgreementMutation } from "../../hooks/useUploadAgreementMutation"
import { AgreementDetailsFragment } from "../../../../gql/graphql"

interface UploadAgreementModalProps {
  agreements: AgreementDetailsFragment[]
  setShowModal: (show: boolean) => void
  employment: Employment
  isOpen: boolean
}

interface UploadAgreementValues {
  agreementType: AgreementTypeEnum | ""
  agreementState: AgreementStateEnum
  sendNotifications: boolean
  file: FilePayload
  reason?: ReplacementReasonEnum
  notes?: string
}

const defaultValidationSchema = Yup.object({
  agreementType: Yup.string().required("The agreement type is required"),
  file: Yup.object({
    file: Yup.mixed().required("The agreement file is required").nullable(),
  }).required("The agreement file is required"),
})

const validationSchemaWithReplacementSurvey = defaultValidationSchema.shape({
  reason: Yup.string().required("Select a reason"),
  notes: Yup.string().trim().required("Specify the adjustments you made"),
})

export const UploadAgreementModal = ({
  setShowModal,
  agreements,
  employment,
  isOpen,
}: UploadAgreementModalProps) => {
  const [t] = useTranslation("Agreements")
  const toast = useToast()
  const [formError, setFormError] = useState<ReactNode>()
  const [showReplacementSurveySection, setShowReplacementSurveySection] = useState(false)
  const [showSendNotification, setShowSendNotification] = useState(true)
  const { handleChange, handleRemoveFile } = useFileUploadComponent({
    multiple: false,
  })

  const [uploadAgreementMutation, { loading }] = useUploadAgreementMutation()
  const [validationSchema, setValidationSchema] = useState(defaultValidationSchema)

  const form = useForm<UploadAgreementValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      sendNotifications: false,
      agreementState: AgreementStateEnum.Draft,
    },
  })
  const { control, handleSubmit, watch, setValue } = form

  const [agreementState, agreementType] = watch(["agreementState", "agreementType"])

  const agreement = useMemo(
    () => agreements.find(agg => agg.agreementType === agreementType),
    [agreements, agreementType],
  )

  const onSubmit = async (values: UploadAgreementValues) => {
    setFormError(undefined)
    if (!agreement) {
      return
    }
    const response = await uploadAgreementMutation({
      variables: {
        input: {
          employmentId: employment.id,
          agreementId: agreement.id,
          agreementState: values.agreementState,
          fileSignedId: values.file.signedId ?? "",
          sendNotifications: values.sendNotifications,
          agreementType: values.agreementType as AgreementTypeEnum,
          reason: values.reason,
          notes: values.notes,
        },
      },
    })

    const errors = response.data?.uploadAgreement?.errors

    if (errors?.length) {
      try {
        const json = JSON.parse(errors[0]) as string[]

        const errorTranslateComponent = (
          <>
            <span>The following placeholder fields are missing in the document:</span>
            <ul className="list-inside list-disc pl-2">
              {json.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </>
        )
        setFormError(errorTranslateComponent)
      } catch {
        setFormError(errors.join("\n"))
      }
    } else {
      const agreementHumanized = t(`agreementTypeEnum.${values.agreementType}`, { ns: "Common" })
      toast({
        render: (
          <Toast variation="success">
            <Toast.Content>
              {t("uploadAgreementModal.successMessage", { agreementType: agreementHumanized })}
            </Toast.Content>
          </Toast>
        ),
      })
      setShowModal(false)
    }
  }

  const stateAssistiveText = useMemo(() => {
    if (agreementState === AgreementStateEnum.Draft) {
      if (agreement?.isESignEnabled) {
        if (
          ![
            AgreementTypeEnum.OysterServiceAgreement,
            AgreementTypeEnum.TerminationOfServiceAgreement,
          ].includes(agreement.agreementType)
        ) {
          return "Uploading as “Draft” requires customer verification. Once they've verified and shared it with the team member, this will generate a DocuSign envelope."
        } else {
          return "Uploading as “Draft” will require customer verification. Once they've verified it, this will generate a DocuSign envelope."
        }
      } else {
        return "Uploading as “Draft” requires customer verification. Once they've verified it, signatures will need to be collected manually."
      }
    } else if (agreementState === AgreementStateEnum.Signed) {
      return "Please make sure the data in the agreement matches the team member's data in the platform"
    }
  }, [agreementState, agreement])

  /**
   * Remove the agreement types that are already signed / partially signed, and have E-Sign enabled
   */
  const agreementTypeOptions = useMemo(() => {
    // Check which agreements have at least one signer already
    const agreementsThatAreNotPartiallyOrFullySigned = agreements.filter(agg => {
      // We always want OSA to be in the list
      if (agg.agreementType === AgreementTypeEnum.OysterServiceAgreement) return true
      if (!agg.isESignEnabled) return true

      // If there's a Voided event, include the agreement
      const hasVoidedEvent = agg.envelope?.events.some(
        evt => evt.eventType === EnvelopeEventTypeEnum.Voided,
      )
      if (hasVoidedEvent) return true

      const signingEvents = agg.envelope?.events.filter(
        evt => evt.eventType === EnvelopeEventTypeEnum.RecipientSigned,
      )
      return !signingEvents || signingEvents.length === 0 || agg.state === AgreementStateEnum.Signed
    })

    return [
      ...agreementsThatAreNotPartiallyOrFullySigned.map(agreement => ({
        label: t(`agreementTypeEnum.${agreement.agreementType}`, { ns: "Common" }),
        value: agreement.agreementType,
      })),
    ]
  }, [t, agreements])

  const statusOptions = useMemo<RadioOption[]>(() => {
    const draftOption: RadioOption = {
      label: "Draft",
      value: AgreementStateEnum.Draft,
    }
    const signedOption: RadioOption = { label: "Signed", value: AgreementStateEnum.Signed }

    // Default options if there's no agreement
    if (!agreement) {
      return [draftOption, signedOption]
    }

    // In case there's an agreement selected
    const agreementHasESign = agreement.isESignEnabled
    const envelopeIsCreated = Boolean(agreement.envelope)
    const isOSA = agreement.agreementType === AgreementTypeEnum.OysterServiceAgreement

    const OSAESignDisableCondition = isOSA && agreementHasESign

    if (OSAESignDisableCondition) {
      signedOption.disabled = true
      signedOption.tooltip =
        "To upload agreement as “Signed”, disable Electronic signature for this agreement in the Actions menu"
    } else {
      // We first start with the easiest case: the agreement doesn't have eSign enabled
      if (!agreementHasESign) {
        if (
          agreement.state === AgreementStateEnum.ReadyToSign &&
          agreement.agreementType !== AgreementTypeEnum.OysterServiceAgreement &&
          agreement.agreementType !== AgreementTypeEnum.TerminationOfServiceAgreement
        ) {
          throw new Error(
            `Agreement: ${agreement.id} - Agreement should never be in "Ready to sign" if it doesn't have E-Sign enabled unless it's OSA or TOSA`,
          )
        }
        // If the agreement is already signed, only allow uploading as "Signed"
        if (agreement.state === AgreementStateEnum.Signed) {
          draftOption.disabled = true
        }
      }
      // When E-Sign is enabled
      if (agreementHasESign) {
        if (!envelopeIsCreated) {
          if (
            [AgreementStateEnum.Signed, AgreementStateEnum.ReadyToSign].includes(agreement.state)
          ) {
            throw new Error(
              `Agreement: ${agreement.id} - Agreement should never be in "Signed" or "Ready to sign" if it doesn't have an envelope`,
            )
          } else {
            signedOption.disabled = true
            // Only show the tooltip if the agreement is not in Ready_to_sign or Signed (doesn't have an envelope)
            signedOption.tooltip =
              "To upload agreement as “Signed”, disable Electronic signature for this agreement in the Actions menu"
          }
        } else {
          if (
            !agreement.envelope ||
            (agreement.envelope.state !== EnvelopeStateEnum.Signed &&
              agreement.envelope.state !== EnvelopeStateEnum.Voided)
          ) {
            signedOption.disabled = true
          }
        }
      }
    }

    return [draftOption, signedOption]
  }, [agreement])

  /**
   * Effect to make sure that if one of the options is disabled, we
   * select the other one by default
   */
  useEffect(() => {
    const draftOption = statusOptions.find(option => option.value === AgreementStateEnum.Draft)
    const signedOption = statusOptions.find(option => option.value === AgreementStateEnum.Signed)
    if (draftOption?.disabled) {
      setValue("agreementState", AgreementStateEnum.Signed)
    }
    if (signedOption?.disabled) {
      setValue("agreementState", AgreementStateEnum.Draft)
    }
  }, [statusOptions, setValue])

  /**
   * Conditions to show the replacement survey:
   * 1. The agreement is automated
   * 2. The agreement is not an Oyster Service Agreement or Termination of Service Agreement
   * 3. The agreement is being replaced (has a previous file)
   */
  useEffect(() => {
    if (
      agreement?.isAutomated &&
      agreement.agreementType !== AgreementTypeEnum.OysterServiceAgreement &&
      agreement.agreementType !== AgreementTypeEnum.TerminationOfServiceAgreement &&
      agreement.lastUpdate?.file
    ) {
      setShowReplacementSurveySection(true)
      setValidationSchema(validationSchemaWithReplacementSurvey)
    } else {
      setShowReplacementSurveySection(false)
      setValidationSchema(defaultValidationSchema)
    }
  }, [agreement, setValidationSchema])

  // Do not show Send Notification switch if agreement is a Termination of Service
  useEffect(() => {
    if (agreement?.agreementType === AgreementTypeEnum.TerminationOfServiceAgreement) {
      setShowSendNotification(false)
    } else {
      setShowSendNotification(true)
    }
  }, [agreement])

  return (
    <Modal rootId="modal" isOpen={isOpen} onRequestClose={() => setShowModal(false)}>
      <Modal.Header>
        <span className="text-2xl">Upload agreement</span>
      </Modal.Header>
      <Modal.Content>
        <form id="upload-agreement-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Controller
              name="agreementType"
              control={control}
              render={({ field: { name, onChange, value, onBlur }, fieldState: { error } }) => (
                <Select
                  label={<FormLabel label="Agreement type" />}
                  error={error?.message}
                  placeholder="Select the agreement type"
                  onChange={onChange}
                  name={name}
                  value={value}
                  onBlur={onBlur}
                >
                  {agreementTypeOptions.map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              )}
            />
            <WarningNotificationBanner
              agreement={agreement}
              employment={employment}
              agreementState={agreementState}
            />
          </div>
          <div className="mb-6">
            <Controller
              control={control}
              name="agreementState"
              render={({ field: { value, ref: _, ...rest } }) => (
                <RadioList
                  {...rest}
                  label={<FormLabel label="Agreement status" />}
                  orientation="horizontal"
                >
                  {statusOptions.map(status =>
                    status.tooltip ? (
                      <Tooltip value={status.tooltip} key={status.value}>
                        <Radio
                          id={status.label}
                          name={status.label}
                          value={status.value}
                          label={<FormLabel label={status.label} />}
                          disabled={status.disabled}
                          checked={agreementState === status.value}
                        />
                      </Tooltip>
                    ) : (
                      <Radio
                        id={status.label}
                        key={status.value}
                        value={status.value}
                        label={<FormLabel label={status.label} />}
                        disabled={status.disabled}
                        checked={agreementState === status.value}
                      />
                    ),
                  )}
                </RadioList>
              )}
            />
            {stateAssistiveText && (
              <div className="mt-3">
                <span className="text-gray-400">{stateAssistiveText}</span>
              </div>
            )}
          </div>

          <div className="mb-6">
            <Controller
              name="file"
              control={control}
              render={({ field: { name, value, onChange } }) => {
                return (
                  <FileUpload
                    id={name}
                    name={name}
                    onChange={handleChange(([file]) => {
                      setFormError(undefined)
                      onChange(file)
                    })}
                    placeholder="Choose file"
                    accept="application/pdf"
                    error={form.formState.errors["file"]?.file?.message}
                    label={
                      <FormLabel
                        label="Upload document"
                        htmlFor={name}
                        assistiveText={
                          <div className="flex flex-col text-sm font-normal leading-4 text-gray-400">
                            <span>Accepted file types: PDF</span>
                            {agreement?.requiredPlaceholders && (
                              <span>
                                Placeholders required: {agreement.requiredPlaceholders.join(", ")}
                              </span>
                            )}
                          </div>
                        }
                      />
                    }
                  >
                    {value && (
                      <FileUpload.Item
                        key={value.id}
                        href={value.href}
                        isLoading={[FileStatus.Idle, FileStatus.Uploading].includes(value.status)}
                        onRemove={() => {
                          handleRemoveFile(() => {
                            onChange(undefined)
                            setFormError(undefined)
                          })(value.id)
                        }}
                      >
                        {value.file.name}
                      </FileUpload.Item>
                    )}
                  </FileUpload>
                )
              }}
            />
          </div>

          {!!formError && (
            <Text size="small" className="mb-6 text-error-base">
              {formError}
            </Text>
          )}
          <div
            className={classNames({
              "mb-6": showReplacementSurveySection,
            })}
          >
            {showSendNotification && (
              <Controller
                name="sendNotifications"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Switch
                    label="Send email notification to customer?"
                    checked={value}
                    onChange={onChange}
                  />
                )}
              />
            )}
          </div>

          {showReplacementSurveySection && <ReplacementSurveySection form={form} />}
        </form>
      </Modal.Content>
      <Modal.Footer className="justify-end">
        <Button disabled={loading} variation="secondary" onClick={() => setShowModal(false)}>
          Cancel
        </Button>
        <Button form="upload-agreement-form" type="submit" isLoading={loading}>
          {agreement?.lastUpdate?.file ? "Replace agreement" : "Add agreement"}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

enum ReplacementReasonEnum {
  "addCustomLanguage" = "addCustomLanguage",
  "includeStandardOptions" = "includeStandardOptions",
  "errorsInAutomatedDocuments" = "errorsInAutomatedDocuments",
  "indirectHire" = "indirectHire",
  "other" = "other",
}

type ReplacementSurveySectionProps = {
  form: UseFormReturn<UploadAgreementValues>
}

function ReplacementSurveySection({ form }: ReplacementSurveySectionProps) {
  const [t] = useTranslation("Agreements", {
    keyPrefix: "uploadAgreementModal.replacementSurveySection",
  })

  return (
    <div className="flex flex-col space-y-4 border-t border-gray-200 pb-1 pt-6">
      <div className="space-y-1">
        <Text className="font-semibold text-black">{t("title")}</Text>
        <Text className="text-sm text-gray-400">{t("description")}</Text>
      </div>
      <Controller
        name="reason"
        control={form.control}
        render={({ field, fieldState: { error } }) => (
          <Select
            onChange={field.onChange}
            value={field.value}
            name={field.name}
            error={error?.message}
            label={t("replacementReasonLabel")}
            placeholder={t("replacementReasonPlaceholder")}
          >
            {Object.values(ReplacementReasonEnum).map(reason => (
              <Select.Option key={reason} value={reason}>
                {t(`replacementReasonEnum.${reason}`)}
              </Select.Option>
            ))}
          </Select>
        )}
      />

      <TextArea
        label={t("notesLabel")}
        placeholder={t("notesPlaceholder")}
        {...form.register("notes")}
        error={form.formState.errors["notes"]?.message}
      />
    </div>
  )
}
